import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { determineGstType, getEntityState, getEntityGstin, gstBreakdownLines } from '@/utils/gstUtils';
import { 
  Plus, Search, Edit2, Trash2, Send, FileCheck, Eye, 
  Clock, CheckCircle, XCircle, Building2, User, Calendar,
  IndianRupee, Package, Filter, RefreshCw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-700 hover:text-color-white', icon: Clock },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  finalized: { label: 'Finalized', color: 'bg-blue-100 text-blue-700', icon: FileCheck },
  expired: { label: 'Expired', color: 'bg-red-100 text-red-700', icon: XCircle }
};

const BILLING_CYCLES = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'half_yearly', label: 'Half Yearly' },
  { value: 'yearly', label: 'Yearly' },
  { value: 'two_years', label: '2 Years' },
  { value: 'three_years', label: '3 Years' }
];

const BILLING_CYCLE_MIN_DAYS = { monthly: 30, quarterly: 90, half_yearly: 180, yearly: 365, two_years: 730, three_years: 1095 };

export default function AdminQuotations() {
  const { canDelete, hasPermission } = useAuth();
  const [quotations, setQuotations] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Permission checks
  const canCreateQuotation = hasPermission('quotations:create');
  const canEditQuotation = hasPermission('quotations:edit');
  const canDeleteQuotation = hasPermission('quotations:delete') && canDelete;
  const canSendQuotation = hasPermission('quotations:send');
  
  // Form state
  const [formData, setFormData] = useState({
    client_id: '',
    category: '',
    product_id: '',
    billing_cycle: 'monthly',
    quantity: 1,
    unit_price: '',
    validity_days: 7,
    notes: ''
  });
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const fetchQuotations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/quotations`);
      setQuotations(response.data);
    } catch (error) {
      toast.error('Failed to fetch quotations');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchClients = useCallback(async () => {
    try {
      // Task 5: Admin quotations only for admin-direct clients
      const response = await axios.get(`${API}/admin/clients-direct`);
      setClients(response.data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      // Fallback - get unique categories from products
      const uniqueCategories = [...new Set(products.map(p => p.category))];
      setCategories(uniqueCategories.map(c => ({ name: c })));
    }
  }, [products]);

  useEffect(() => {
    fetchQuotations();
    fetchClients();
    fetchProducts();
  }, [fetchQuotations, fetchClients, fetchProducts]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Get products filtered by selected category
  const filteredProducts = formData.category
    ? products.filter(p => p.category === formData.category && p.is_active)
    : products.filter(p => p.is_active);

  // Get product pricing for selected product
  const selectedProduct = products.find(p => p.id === formData.product_id);
  const productPrice = selectedProduct?.billing_prices?.[formData.billing_cycle] || selectedProduct?.base_price || 0;

  const resetForm = () => {
    setFormData({ client_id: '', category: '', product_id: '', billing_cycle: 'monthly', quantity: 1, unit_price: '', validity_days: 30, notes: '' });
    setEditMode(false);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.client_id || !formData.product_id) {
      toast.error('Please select a client and product');
      return;
    }
    const minDays = BILLING_CYCLE_MIN_DAYS[formData.billing_cycle] || 30;
    if ((parseInt(formData.validity_days) || 0) < minDays) {
      toast.error(`Minimum validity for ${formData.billing_cycle} is ${minDays} days`);
      return;
    }
    try {
      const payload = {
        client_id: formData.client_id,
        product_id: formData.product_id,
        billing_cycle: formData.billing_cycle,
        quantity: parseInt(formData.quantity) || 1,
        validity_days: parseInt(formData.validity_days) || minDays,
        notes: formData.notes || null
      };
      if (formData.unit_price && parseFloat(formData.unit_price) > 0) {
        payload.unit_price = parseFloat(formData.unit_price);
      }
      if (editMode && editingId) {
        await axios.put(`${API}/admin/quotations/${editingId}`, {
          quantity: payload.quantity,
          unit_price: payload.unit_price || null,
          validity_days: payload.validity_days,
          notes: payload.notes
        });
        toast.success('Quotation updated successfully');
      } else {
        await axios.post(`${API}/admin/quotations`, payload);
        toast.success('Quotation created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchQuotations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save quotation');
    }
  };

  const handleEdit = (quotation) => {
    setEditMode(true);
    setEditingId(quotation.id);
    const product = products.find(p => p.id === quotation.product_id);
    setFormData({
      client_id: quotation.client_id,
      category: product?.category || '',
      product_id: quotation.product_id,
      billing_cycle: quotation.billing_cycle,
      quantity: quotation.quantity,
      unit_price: quotation.unit_price?.toString() || '',
      validity_days: quotation.validity_days,
      notes: quotation.notes || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (quotationId) => {
    if (!canDelete) {
      toast.error('Only Admin or Support Admin can delete quotations');
      return;
    }
    if (!confirm('Are you sure you want to delete this quotation?')) return;
    
    try {
      await axios.delete(`${API}/admin/quotations/${quotationId}`);
      toast.success('Quotation deleted');
      fetchQuotations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete quotation');
    }
  };

  const handleSend = async (quotationId) => {
    try {
      await axios.post(`${API}/admin/quotations/${quotationId}/send`);
      toast.success('Quotation sent to client');
      fetchQuotations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send quotation');
    }
  };

  const handleFinalize = async (quotationId) => {
    if (!confirm('This will convert the quotation to an order. Continue?')) return;
    
    try {
      const response = await axios.post(`${API}/admin/quotations/${quotationId}/finalize`);
      toast.success(`Order ${response.data.order_id} created successfully`);
      fetchQuotations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to finalize quotation');
    }
  };

  const handleView = (quotation) => {
    setSelectedQuotation(quotation);
    setViewDialogOpen(true);
  };

  // Filter quotations
  const filteredQuotations = quotations.filter(q => {
    const matchesSearch = 
      q.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.client?.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.client?.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || q.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const isExpired = (validUntil) => {
    if (!validUntil) return false;
    return new Date(validUntil) < new Date();
  };

  const stats = {
    total: quotations.length,
    draft: quotations.filter(q => q.status === 'draft').length,
    approved: quotations.filter(q => q.status === 'approved').length,
    finalized: quotations.filter(q => q.status === 'finalized').length
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap md-flex-nowrap">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Quotations</h1>
            <p className="text-slate-500">Create and manage quotations for clients</p>
          </div>
          <div className="flex items-center gap-2 mt-2 md:m-0">
            <button onClick={fetchQuotations} className="p-2 rounded-lg hover:bg-slate-100 border border-slate-200" title="Refresh" data-testid="refresh-btn"><RefreshCw className="h-4 w-4 text-slate-500" /></button>
            {canCreateQuotation && (
              <Button
                onClick={() => { resetForm(); setDialogOpen(true); }}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="create-quotation-button"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Quotation
              </Button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="cursor-pointer hover:shadow-md" onClick={() => setStatusFilter('all')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
                <Package className="h-8 w-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className={`cursor-pointer hover:shadow-md ${statusFilter === 'draft' ? 'ring-2 ring-slate-500' : ''}`} onClick={() => setStatusFilter('draft')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Draft</p>
                  <p className="text-2xl font-bold text-slate-600">{stats.draft}</p>
                </div>
                <Clock className="h-8 w-8 text-slate-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className={`cursor-pointer hover:shadow-md ${statusFilter === 'approved' ? 'ring-2 ring-green-500' : ''}`} onClick={() => setStatusFilter('approved')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Approved</p>
                  <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className={`cursor-pointer hover:shadow-md ${statusFilter === 'finalized' ? 'ring-2 ring-blue-500' : ''}`} onClick={() => setStatusFilter('finalized')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Finalized</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.finalized}</p>
                </div>
                <FileCheck className="h-8 w-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-auto bg-white" data-testid="status-filter">
                  <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4" />
                    {/* Yeh text mobile par hide rahega aur medium (md) screen par show hoga */}
                    <span className="hidden md:inline">
                      <SelectValue placeholder="Status" />
                    </span>
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="finalized">Finalized</SelectItem>
                </SelectContent>
              </Select>
            </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search quotations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 w-44 md:w-64 !text-sm md:!text-base"
                  data-testid="search-quotations"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quotations Table */}
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : filteredQuotations.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Package className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <p>No quotations found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left p-4 text-sm font-medium text-slate-600 text-nowrap">Client</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-600 text-nowrap">Product</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-600 text-nowrap">Amount</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-600 text-nowrap">Valid Until</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-600 text-nowrap">Status</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-600 text-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredQuotations.map((quotation) => {
                      const status = STATUS_CONFIG[quotation.status] || STATUS_CONFIG.draft;
                      const StatusIcon = status.icon;
                      const expired = isExpired(quotation.valid_until) && quotation.status === 'draft';
                      
                      return (
                        <tr key={quotation.id} className="hover:bg-slate-50" data-testid={`quotation-row-${quotation.id}`}>
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                                <User className="h-5 w-5 text-blue-600" />
                              </div>
                              <div>
                                <p className="font-medium text-slate-800">{quotation.client?.company_name || 'Unknown'}</p>
                                <p className="text-sm text-slate-500">{quotation.client?.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="p-4">
                            <div>
                              <p className="font-medium text-slate-800 text-nowrap">{quotation.product_name}</p>
                              <p className="text-sm text-slate-500">{quotation.billing_cycle} × {quotation.quantity}</p>
                            </div>
                          </td>
                          <td className="p-4">
                            <p className="font-semibold text-slate-800">{formatCurrency(quotation.total_amount)}</p>
                            <p className="text-xs text-slate-400 mt-0.5">incl. 18% GST</p>
                            <p className="text-xs text-slate-500">{formatCurrency(quotation.unit_price)}/unit</p>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-2 text-nowrap">
                              <Calendar className="h-4 w-4 text-slate-400" />
                              <span className={expired ? 'text-red-500' : 'text-slate-600'}>
                                {formatDate(quotation.valid_until)}
                              </span>
                              {expired && <Badge variant="destructive" className="text-xs">Expired</Badge>}
                            </div>
                          </td>
                          <td className="p-4">
                            <Badge className={status.color}>
                              <StatusIcon className="h-3 w-3 mr-1" />
                              {status.label}
                            </Badge>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleView(quotation)}
                                className="h-8 w-8 p-0"
                                data-testid={`view-${quotation.id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              {quotation.status === 'draft' && (
                                <>
                                  {canEditQuotation && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleEdit(quotation)}
                                      className="h-8 w-8 p-0"
                                      data-testid={`edit-${quotation.id}`}
                                    >
                                      <Edit2 className="h-4 w-4" />
                                    </Button>
                                  )}
                                  {canDeleteQuotation && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDelete(quotation.id)}
                                      className="h-8 w-8 p-0 text-red-600"
                                      data-testid={`delete-${quotation.id}`}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  )}
                                </>
                              )}
                              {quotation.status === 'approved' && canEditQuotation && (
                                <Button
                                  size="sm"
                                  onClick={() => handleFinalize(quotation.id)}
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                  data-testid={`finalize-${quotation.id}`}
                                >
                                  <FileCheck className="h-4 w-4 mr-1" />
                                  Finalize
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Quotation Dialog */}
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col">
            <DialogHeader>
              <DialogTitle>{editMode ? 'Edit Quotation' : 'Create Quotation'}</DialogTitle>
              <DialogDescription>
                {editMode ? 'Update the quotation details below.' : 'Create a new quotation for a client.'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 overflow-y-auto flex-1 pr-1">
              {/* Client Selection */}
              <div className="space-y-2">
                <Label>Select Client *</Label>
                <Select 
                  value={formData.client_id} 
                  onValueChange={(value) => setFormData({ ...formData, client_id: value })}
                  disabled={editMode}
                >
                  <SelectTrigger data-testid="client-select">
                    <SelectValue placeholder="Choose a client..." />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-slate-400" />
                          <span>{client.company_name || client.email}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Category Selection (for filtering products) */}
              <div className="space-y-2">
                <Label>Category</Label>
                <Select 
                  value={formData.category || "all"} 
                  onValueChange={(value) => setFormData({ ...formData, category: value === "all" ? '' : value, product_id: '' })}
                  disabled={editMode}
                >
                  <SelectTrigger data-testid="category-select">
                    <SelectValue placeholder="Filter by category (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id || cat.name} value={cat.name}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.category && (
                  <p className="text-xs text-slate-500">
                    Showing {filteredProducts.length} products in {formData.category}
                  </p>
                )}
              </div>

              {/* Product Selection */}
              <div className="space-y-2">
                <Label>Select Product *</Label>
                <Select 
                  value={formData.product_id} 
                  onValueChange={(value) => {
                    const prod = products.find(p => p.id === value);
                    const enabledCycles = prod?.enabled_billing_cycles && prod.enabled_billing_cycles.length > 0
                      ? prod.enabled_billing_cycles
                      : BILLING_CYCLES.map(c => c.value);
                    const newCycle = enabledCycles.includes(formData.billing_cycle)
                      ? formData.billing_cycle
                      : (enabledCycles[0] || 'monthly');
                    const minDays = BILLING_CYCLE_MIN_DAYS[newCycle] || 30;
                    setFormData(prev => ({ ...prev, product_id: value, billing_cycle: newCycle, validity_days: Math.max(prev.validity_days || 0, minDays) }));
                  }}
                  disabled={editMode}
                >
                  <SelectTrigger data-testid="product-select">
                    <SelectValue placeholder="Choose a product..." />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredProducts.map((product) => (
                      <SelectItem key={product.id} value={product.id}>
                        <div className="flex items-center justify-between w-full">
                          <span>{product.name}</span>
                          <span className="text-slate-400 text-xs ml-2">({product.category})</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Billing Cycle & Quantity */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Billing Cycle</Label>
                  <Select 
                    value={formData.billing_cycle} 
                    onValueChange={(value) => {
                      const minDays = BILLING_CYCLE_MIN_DAYS[value] || 30;
                      setFormData(prev => ({ ...prev, billing_cycle: value, validity_days: Math.max(prev.validity_days || 0, minDays) }));
                    }}
                    disabled={editMode}
                  >
                    <SelectTrigger data-testid="billing-cycle-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(() => {
                        const enabledCycles = selectedProduct?.enabled_billing_cycles && selectedProduct.enabled_billing_cycles.length > 0
                          ? selectedProduct.enabled_billing_cycles
                          : BILLING_CYCLES.map(c => c.value);
                        return BILLING_CYCLES.filter(c => enabledCycles.includes(c.value)).map((cycle) => (
                          <SelectItem key={cycle.value} value={cycle.value}>{cycle.label}</SelectItem>
                        ));
                      })()}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Quantity</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                    data-testid="quantity-input"
                  />
                </div>
              </div>

              {/* Product Features (shown when a product is selected) */}
              {selectedProduct && selectedProduct.features && selectedProduct.features.length > 0 && (
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 space-y-1 mt-2" data-testid="product-features-panel">
                  <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Product Features</p>
                  <ul className="space-y-1">
                    {selectedProduct.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-blue-800">
                        <span className="text-blue-500 mt-0.5">✓</span> {f}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Price */}
              <div className="space-y-2">
                <Label>Unit Price (optional - leave empty for default pricing)</Label>
                <div className="relative">
                  <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.unit_price}
                    onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })}
                    placeholder={`Default: ${formatCurrency(productPrice)}`}
                    className="pl-9"
                    data-testid="price-input"
                  />
                </div>
                {selectedProduct && (
                  <p className="text-xs text-slate-500">
                    Default price for {formData.billing_cycle}: {formatCurrency(productPrice)}
                  </p>
                )}
              </div>

              {/* Validity Days */}
              <div className="space-y-2">
                <Label>Validity (days)</Label>
                <Input
                  type="number"
                  min="1"
                  max="90"
                  value={formData.validity_days}
                  onChange={(e) => setFormData({ ...formData, validity_days: e.target.value })}
                  data-testid="validity-input"
                />
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes for the client..."
                  rows={2}
                  data-testid="notes-input"
                />
              </div>

              {/* GST Breakdown Preview */}
              {(formData.product_id || (parseFloat(formData.unit_price) > 0)) && (
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 space-y-1.5 text-sm" data-testid="gst-breakdown-preview">
                  {(() => {
                    const up = parseFloat(formData.unit_price) > 0 ? parseFloat(formData.unit_price) : productPrice;
                    const qty = parseInt(formData.quantity) || 1;
                    const sub = Math.round(up * qty * 100) / 100;
                    const gst = Math.round(sub * 0.18 * 100) / 100;
                    const total = Math.round((sub + gst) * 100) / 100;
                    const selClient = clients.find(c => c.id === formData.client_id);
                    const gstType = determineGstType(getEntityGstin(selClient), getEntityState(selClient));
                    const half = Math.round(gst / 2 * 100) / 100;
                    const entityState = getEntityState(selClient);
                    const entityGstin = getEntityGstin(selClient);
                    return (
                      <>
                        {/* GST Type Badge + Missing State Warning */}
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${gstType === 'CGST+SGST' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                            {gstType} Applied
                          </span>
                          {entityState && <span className="text-xs text-slate-400">({entityState})</span>}
                        </div>
                        {!entityState && !entityGstin && (
                          <div className="text-xs text-amber-600 flex items-center gap-1 mb-1">
                            ⚠ State not set for this client — IGST applied by default. Ask client to update their profile state.
                          </div>
                        )}
                        <div className="flex justify-between text-slate-500">
                          <span>Sub Total</span>
                          <span className="font-medium">₹{sub.toLocaleString('en-IN', {minimumFractionDigits:2})}</span>
                        </div>
                        {gstType === 'CGST+SGST' ? (
                          <>
                            <div className="flex justify-between text-slate-500">
                              <span>CGST (9%)</span>
                              <span className="font-medium">₹{half.toLocaleString('en-IN', {minimumFractionDigits:2})}</span>
                            </div>
                            <div className="flex justify-between text-slate-500">
                              <span>SGST (9%)</span>
                              <span className="font-medium">₹{half.toLocaleString('en-IN', {minimumFractionDigits:2})}</span>
                            </div>
                          </>
                        ) : (
                          <div className="flex justify-between text-slate-500">
                            <span>IGST (18%)</span>
                            <span className="font-medium">₹{gst.toLocaleString('en-IN', {minimumFractionDigits:2})}</span>
                          </div>
                        )}
                        <div className="flex justify-between border-t border-blue-200 pt-1.5 font-semibold text-slate-800">
                          <span>Grand Total</span>
                          <span className="text-blue-700">₹{total.toLocaleString('en-IN', {minimumFractionDigits:2})}</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}

              {/* Submit */}
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="save-quotation-button">
                  {editMode ? 'Update' : 'Create'} Quotation
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* View Quotation Dialog */}
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col">
            <DialogHeader>
              <DialogTitle>Quotation Details</DialogTitle>
            </DialogHeader>
            {selectedQuotation && (
              <div className="space-y-4 overflow-y-auto flex-1 pr-1">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Client</p>
                    <p className="font-medium">{selectedQuotation.client?.company_name || 'N/A'}</p>
                  </div>
                 <div>
                    <p className="text-sm text-slate-500">Status</p>
                    <Badge 
                      className={`${STATUS_CONFIG[selectedQuotation.status]?.color || ''} hover:text-white`}
                    >
                      {STATUS_CONFIG[selectedQuotation.status]?.label}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Product</p>
                    <p className="font-medium">{selectedQuotation.product_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Billing Cycle</p>
                    <p className="font-medium capitalize">{selectedQuotation.billing_cycle}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Unit Price</p>
                    <p className="font-medium">{formatCurrency(selectedQuotation.unit_price)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Quantity</p>
                    <p className="font-medium">{selectedQuotation.quantity}</p>
                  </div>
                  {/* GST Breakdown */}
                  <div className="col-span-2 bg-blue-50 border border-blue-100 rounded-lg p-4 space-y-1.5 text-sm" data-testid="gst-breakdown-view">
                    {(() => {
                      const sub = selectedQuotation.subtotal > 0
                        ? selectedQuotation.subtotal
                        : Math.round((selectedQuotation.unit_price || 0) * (selectedQuotation.quantity || 1) * 100) / 100;
                      const gst = selectedQuotation.gst_amount > 0
                        ? selectedQuotation.gst_amount
                        : Math.round(sub * 0.18 * 100) / 100;
                      const total = selectedQuotation.total_amount || Math.round((sub + gst) * 100) / 100;
                      const gstType = selectedQuotation.gst_type || 'IGST';
                      const half = Math.round(gst / 2 * 100) / 100;
                      const entityStateStored = selectedQuotation.entity_state_at_time_of_creation || '';
                      return (
                        <>
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${gstType === 'CGST+SGST' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                              {gstType} Applied
                            </span>
                            {entityStateStored && <span className="text-xs text-slate-400">({entityStateStored})</span>}
                          </div>
                          <div className="flex justify-between text-slate-500">
                            <span>Sub Total</span>
                            <span className="font-medium">₹{sub.toLocaleString('en-IN', {minimumFractionDigits:2})}</span>
                          </div>
                          {gstType === 'CGST+SGST' ? (
                            <>
                              <div className="flex justify-between text-slate-500">
                                <span>CGST (9%)</span>
                                <span className="font-medium">₹{half.toLocaleString('en-IN', {minimumFractionDigits:2})}</span>
                              </div>
                              <div className="flex justify-between text-slate-500">
                                <span>SGST (9%)</span>
                                <span className="font-medium">₹{half.toLocaleString('en-IN', {minimumFractionDigits:2})}</span>
                              </div>
                            </>
                          ) : (
                            <div className="flex justify-between text-slate-500">
                              <span>IGST (18%)</span>
                              <span className="font-medium">₹{gst.toLocaleString('en-IN', {minimumFractionDigits:2})}</span>
                            </div>
                          )}
                          <div className="flex justify-between border-t border-blue-200 pt-1.5 font-semibold text-slate-800">
                            <span>Grand Total</span>
                            <span className="text-green-600">₹{total.toLocaleString('en-IN', {minimumFractionDigits:2})}</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Valid Until</p>
                    <p className="font-medium">{formatDate(selectedQuotation.valid_until)}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm text-slate-500">Created At</p>
                    <p className="font-medium">{formatDate(selectedQuotation.created_at)}</p>
                  </div>
                  {selectedQuotation.notes && (
                    <div className="col-span-2">
                      <p className="text-sm text-slate-500">Notes</p>
                      <p className="font-medium">{selectedQuotation.notes}</p>
                    </div>
                  )}
                </div>
                {/* Product features in view dialog */}
                {(() => {
                  const prod = products.find(p => p.id === selectedQuotation.product_id);
                  return prod?.features?.length > 0 ? (
                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 space-y-1 mt-2">
                      <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Product Features</p>
                      <ul className="space-y-1">
                        {prod.features.map((f, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-blue-800">
                            <span className="text-blue-500 mt-0.5">✓</span> {f}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null;
                })()}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
